import React, {
  type ElementType,
  forwardRef,
  type FunctionComponent,
} from 'react';
import classNames from 'classnames';
import ButtonBase, {
  ButtonBaseProps,
  ExtendableButtonBaseProps,
} from '../ButtonBase';
import IconButton, { IconButtonProps } from '../IconButton';
import IconArrowLeft16 from '../icons/IconArrowLeft16';
import IconArrowLeft24 from '../icons/IconArrowLeft24';
import IconArrowRight16 from '../icons/IconArrowRight16';
import IconArrowRight24 from '../icons/IconArrowRight24';
import { PolymorphicRef } from '../_internal/components';
import {
  getResponsiveValue,
  processStylingProps,
  ResponsiveValue,
} from '../_internal/styling';

import useScreenSize from '../_internal/useScreenSize';
import { Variant, Size } from './utils';
import styles from './paginationItem.module.scss';
import Text from '../Text';

const IconArrow = ({ size, variant }: { size: Size; variant: Variant }) => {
  if (size === 'large') {
    return variant === 'previous' ? <IconArrowLeft24 /> : <IconArrowRight24 />;
  }

  return variant === 'previous' ? <IconArrowLeft16 /> : <IconArrowRight16 />;
};

interface Props {
  /**
   * Label for the pagination item
   */
  'aria-label': string;
  /**
   * The color variant of the pagination item
   *
   * @default 'default'
   */
  color?: ResponsiveValue<'default' | 'inverse'>;
  /**
   * Whether or not the pagination button is selected
   *
   * @default false
   */
  isSelected?: boolean;
  /**
   * The page that the pagination item will select
   */
  page?: number;
  /**
   * The size of the pagination item. This value is responsive and may be configured for multiple screen sizes.
   *
   */
  size?: ResponsiveValue<Size>;
  /**
   * Type/variant of the pagination item
   */
  variant: Variant;
}

export type PaginationItemProps<C extends ElementType = 'button'> =
  ExtendableButtonBaseProps<C, Props>;

type PaginationItemComponent = FunctionComponent &
  (<C extends ElementType = 'button'>(
    props: PaginationItemProps<C>,
  ) => React.ReactElement | null);

export const PaginationItem: PaginationItemComponent = forwardRef(
  <C extends ElementType = 'button'>(
    {
      'aria-label': ariaLabel,
      color = 'default',
      disabled,
      isSelected = false,
      page,
      size = 'large',
      variant,
      ...rootProps
    }: PaginationItemProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const screenSize = useScreenSize();
    const responsiveColor = getResponsiveValue(screenSize, color);
    const responsiveSize = getResponsiveValue(screenSize, size, 'large');
    const buttonProps = processStylingProps(rootProps, 'PaginationItem', {
      stylingProps: 'remove',
      dangerousStylingProps: 'remove',
    });

    if (variant === 'previous' || variant === 'next') {
      const iconButtonProps = {
        ...buttonProps,
        'aria-label': ariaLabel,
        children: <IconArrow size={responsiveSize} variant={variant} />,
      } as IconButtonProps<C>;

      return (
        <IconButton
          variant={responsiveColor === 'inverse' ? 'inherited' : undefined}
          ref={ref}
          disabled={disabled}
          {...iconButtonProps}
        />
      );
    }

    if (variant === 'before-ellipsis' || variant === 'after-ellipsis') {
      return (
        <Text
          as="div"
          className={classNames(styles.ellipsis, styles[responsiveSize], {
            [styles['is-disabled']]: disabled,
          })}
          height="short"
          family="regular"
          scale="3"
          align="center"
          aria-label={ariaLabel}
          role="img"
        >
          &hellip;
        </Text>
      );
    }

    const className = classNames(styles.page, styles[responsiveSize], {
      [styles['is-inverse']]: responsiveColor === 'inverse',
      [styles['is-selected']]: isSelected,
    });

    return (
      <ButtonBase
        className={className}
        aria-label={ariaLabel}
        disabled={disabled}
        ref={ref}
        {...(buttonProps as ButtonBaseProps<C>)}
      >
        {page}
      </ButtonBase>
    );
  },
);

PaginationItem.displayName = 'PaginationItem';

export default PaginationItem;
